/**
 * 홈 위젯 글씨 크기 — 사용자가 고르는 한 단.
 *
 * 화면 크기에 따른 보정은 CSS 가 이미 하고 있다(`index.css` 의 `--fs-*` 두 밴드). 여기서 정하는
 * 것은 그 위에 곱하는 <b>배율 하나</b>(`--fs-scale`)뿐이라, 이 파일은 «어느 단을 골랐는지» 를
 * 기억하고 `<html data-fs="...">` 에 얹는 일만 한다. 실제 크기는 전부 CSS 에 있다.
 *
 * ## 왜 서버가 아니라 이 브라우저에 남기나
 *
 * 이것은 계정의 성질이 아니라 <b>지금 보고 있는 화면과 눈</b>의 문제다. 같은 사람이 사무실
 * 27인치에서는 작게, 현장 태블릿에서는 크게 보고 싶을 수 있고, 그때 서버에 저장돼 있으면
 * 한쪽을 맞추는 순간 다른 쪽이 틀어진다. 표를 하나 더 두기에도 가벼운 사실이다.
 *
 * ## 왜 배율만 두고 px 을 두지 않나
 *
 * 크기를 px 로 기억하면 화면 밴드(모바일·노트북·넓은 웹)를 손볼 때마다 사용자가 고른 값이
 * 그 보정을 덮어 버린다. 배율은 밴드 <b>위에</b> 곱해지므로 둘이 싸우지 않는다.
 */

/** 작은 것부터 큰 것까지. 가운데(`md`)가 권장값이자 기본값이다. */
export const FONT_SCALES = ['xs', 'sm', 'md', 'lg', 'xl'] as const;

export type FontScale = (typeof FONT_SCALES)[number];

export const DEFAULT_FONT_SCALE: FontScale = 'md';

/** 고른 단에 붙는 이름. 「권장」 이라고 적는 것은 기본값이 <b>고민 없이 고를 수 있는 답</b>임을 알리기 위해서다. */
export const FONT_SCALE_LABELS: Record<FontScale, string> = {
  xs: '가장 작게',
  sm: '작게',
  md: '권장',
  lg: '크게',
  xl: '가장 크게',
};

const STORAGE_KEY = 'bomi.ui.fontScale';

function isFontScale(value: unknown): value is FontScale {
  return typeof value === 'string' && (FONT_SCALES as readonly string[]).includes(value);
}

export function readFontScale(): FontScale {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isFontScale(raw) ? raw : DEFAULT_FONT_SCALE;
  } catch {
    // 사생활 보호 모드. 기억을 못 할 뿐이라 이번 화면은 권장값으로 뜬다.
    return DEFAULT_FONT_SCALE;
  }
}

/**
 * 화면에 반영한다. `<html>` 에 얹는 이유는 `--fs-scale` 을 읽는 곳이 `:root` 라서다 —
 * 본문 어딘가에 걸면 그 바깥(헤더·모달)이 따라오지 않는다.
 */
export function applyFontScale(scale: FontScale): void {
  document.documentElement.dataset.fs = scale;
}

export function saveFontScale(scale: FontScale): void {
  applyFontScale(scale);
  try {
    window.localStorage.setItem(STORAGE_KEY, scale);
  } catch {
    // 저장에 실패해도 이번 세션 동안은 적용된 채로 남는다. 다음에 권장값으로 돌아올 뿐이다.
  }
}

/**
 * 첫 그림 전에 한 번 부른다({@code main.tsx}).
 *
 * React 가 뜬 뒤에 적용하면 권장 크기로 한 번 그려졌다가 바뀌어, 크게 쓰는 사람에게는 매번
 * 글씨가 <b>튀어 오르는</b> 화면이 된다.
 */
export function initFontScale(): void {
  applyFontScale(readFontScale());
}
